import { clone } from 'ramda';

import {
  EBuildingStatus,
  ECategoryType,
  ECoworkingOfferType,
  EJsonQueryReadyBusinessType,
  EObjectType,
  EOfficeType,
  ERoomType,
  type IJsonQueryType,
} from '../api-models/common/json_query';

import { isCommercial } from './category';
import { DEFAULT_LONGTERM_RENT_OPTION, DISJOINT_AVAILABLE_OFFER_TYPES } from './constants';
import { appendTermsValue, getTermsValue, resetTerms, setRootType, setTerm, setTerms } from './helpers';
import { setDealType } from './setDealType';
import { setRoomTypes } from './setRoomTypes';
import { FDealType, FOfferType, type TJsonQuery } from './types';
import { getDealTypeFromJsonQuery, getOfferTypeFromJsonQuery, isAvailableOfferTypeCombination } from './utils';

export function setOfferType(jsonQuery: TJsonQuery): (nextOfferType: FOfferType) => TJsonQuery {
  return nextOfferType => {
    if (!isAvailableOfferTypeCombination(nextOfferType)) {
      return jsonQuery;
    }

    const nextOfferTypes = getNextOfferTypes(nextOfferType);

    let nextJsonQuery = clone(jsonQuery);

    // убраться лишние свойства
    nextJsonQuery = resetOfferTypeRelatedTerms(nextJsonQuery)(nextOfferType);

    // поочерёдно примениться все offerType из списка выше
    return nextOfferTypes.reduce((jq, offerType) => selectOfferType(jq)(offerType), nextJsonQuery);
  };
}

function getNextOfferTypes(nextOfferType: FOfferType): FOfferType[] {
  let nextOfferTypes = DISJOINT_AVAILABLE_OFFER_TYPES.filter(o => (o & nextOfferType) !== 0);

  if ([FOfferType.FlatOld, FOfferType.FlatNew].every(o => nextOfferTypes.includes(o))) {
    nextOfferTypes = nextOfferTypes.filter(o => (o & FOfferType.Flat) === 0);
    nextOfferTypes.push(FOfferType.Flat);
  }

  return nextOfferTypes;
}

function resetOfferTypeRelatedTerms(jsonQuery: TJsonQuery): (nextOfferType: FOfferType) => TJsonQuery {
  return nextOfferType => {
    let nextJsonQuery = clone(jsonQuery);

    const dealType = getDealTypeFromJsonQuery(jsonQuery);
    const offerType = getOfferTypeFromJsonQuery(jsonQuery);

    nextJsonQuery = resetTerms([
      'building_status',
      'category',
      'object_type',
      'office_type',
      'flat_share',
      'bath',
      'shower',
      'ready_business_types',
      'coworking_offer_type',
    ])(nextJsonQuery);

    if (nextOfferType !== FOfferType.FlatNew) {
      nextJsonQuery = resetTerms(['with_newobject', 'from_developer'])(nextJsonQuery);
    }

    const room = getTermsValue('room')(nextJsonQuery);
    if (room) {
      nextJsonQuery = setRoomTypes(nextJsonQuery)(
        room.filter(r => ![ERoomType.Room, ERoomType.FlatShared, ERoomType.Bed].includes(r)),
      );
    }

    if (isCommercial(offerType) && !isCommercial(nextOfferType)) {
      if (dealType & FDealType.Rent) {
        nextJsonQuery = setTerm('for_day')(nextJsonQuery)(DEFAULT_LONGTERM_RENT_OPTION);
      }
    }

    return nextJsonQuery;
  };
}

function selectOfferType(jsonQuery: TJsonQuery): (nextOfferType: FOfferType) => TJsonQuery {
  return nextOfferType => {
    const dealType = getDealTypeFromJsonQuery(jsonQuery);

    let nextJsonQuery = clone(jsonQuery);

    switch (nextOfferType) {
      case FOfferType.Flat:
        nextJsonQuery = setRootOfferType(nextJsonQuery)('flat');
        break;
      case FOfferType.FlatOld:
        nextJsonQuery = setRootOfferType(nextJsonQuery)('flat');

        if ((dealType & FDealType.Rent) === 0) {
          nextJsonQuery = setTerm('building_status')(nextJsonQuery)(EBuildingStatus.Old);
        }
        break;
      case FOfferType.FlatNew:
        nextJsonQuery = setRootOfferType(nextJsonQuery)('flat');
        nextJsonQuery = setTerm('building_status')(nextJsonQuery)(EBuildingStatus.New);
        nextJsonQuery = setTerm('with_newobject')(nextJsonQuery)(true);
        break;
      case FOfferType.FlatShared:
        nextJsonQuery = setRootOfferType(nextJsonQuery)('flat');
        nextJsonQuery = appendTermsValue(nextJsonQuery)('room', [ERoomType.FlatShared]);
        break;
      case FOfferType.Room:
        nextJsonQuery = setRootOfferType(nextJsonQuery)('flat');
        nextJsonQuery = appendTermsValue(nextJsonQuery)('room', [ERoomType.Room]);
        break;
      case FOfferType.Bed:
        nextJsonQuery = setRootOfferType(nextJsonQuery)('flat');
        nextJsonQuery = appendTermsValue(nextJsonQuery)('room', [ERoomType.Bed]);
        break;
      case FOfferType.House:
        nextJsonQuery = setRootOfferType(nextJsonQuery)('suburban');
        nextJsonQuery = appendTermsValue(nextJsonQuery)('object_type', [EObjectType.House]);
        break;
      case FOfferType.Townhouse:
        nextJsonQuery = setRootOfferType(nextJsonQuery)('suburban');
        nextJsonQuery = appendTermsValue(nextJsonQuery)('object_type', [EObjectType.Townhouse]);
        break;
      case FOfferType.HousePart:
        nextJsonQuery = setRootOfferType(nextJsonQuery)('suburban');
        nextJsonQuery = appendTermsValue(nextJsonQuery)('object_type', [EObjectType.Housepart]);
        break;
      case FOfferType.Land:
        nextJsonQuery = setDealType(nextJsonQuery)(FDealType.Sale);
        nextJsonQuery = setRootOfferType(nextJsonQuery)('suburban');
        nextJsonQuery = appendTermsValue(nextJsonQuery)('object_type', [EObjectType.Land]);
        break;
      case FOfferType.Garage:
        nextJsonQuery = setRootOfferType(nextJsonQuery)('commercial');
        nextJsonQuery = setTerms('office_type')(nextJsonQuery)([EOfficeType.Garage]);
        break;
      case FOfferType.Office:
        nextJsonQuery = setRootOfferType(nextJsonQuery)('commercial');
        nextJsonQuery = appendTermsValue(nextJsonQuery)('office_type', [EOfficeType.Office]);
        break;
      case FOfferType.TradeArea:
        nextJsonQuery = setRootOfferType(nextJsonQuery)('commercial');
        nextJsonQuery = appendTermsValue(nextJsonQuery)('office_type', [EOfficeType.TradeArea]);
        break;
      case FOfferType.Warehouse:
        nextJsonQuery = setRootOfferType(nextJsonQuery)('commercial');
        nextJsonQuery = appendTermsValue(nextJsonQuery)('office_type', [EOfficeType.Warehouse]);
        break;
      case FOfferType.FreeAppointmentObject:
        nextJsonQuery = setRootOfferType(nextJsonQuery)('commercial');
        nextJsonQuery = appendTermsValue(nextJsonQuery)('office_type', [EOfficeType.FreeAppointmentObject]);
        break;
      case FOfferType.PublicCatering:
        nextJsonQuery = setRootOfferType(nextJsonQuery)('commercial');
        nextJsonQuery = appendTermsValue(nextJsonQuery)('office_type', [EOfficeType.PublicCatering]);
        break;
      case FOfferType.Manufacture:
        nextJsonQuery = setRootOfferType(nextJsonQuery)('commercial');
        nextJsonQuery = appendTermsValue(nextJsonQuery)('office_type', [EOfficeType.Manufacture]);
        break;
      case FOfferType.AutoService:
        nextJsonQuery = setRootOfferType(nextJsonQuery)('commercial');
        nextJsonQuery = appendTermsValue(nextJsonQuery)('office_type', [EOfficeType.AutoService]);
        break;
      case FOfferType.ReadyBusiness:
        nextJsonQuery = setRootOfferType(nextJsonQuery)('commercial');
        if (!getTermsValue('office_type')(jsonQuery)?.includes(EOfficeType.Business)) {
          nextJsonQuery = appendTermsValue(nextJsonQuery)('office_type', [EOfficeType.Business]);
        }
        nextJsonQuery = appendTermsValue(nextJsonQuery)('ready_business_types', [
          EJsonQueryReadyBusinessType.ReadyBusiness,
        ]);
        break;
      case FOfferType.RentalBusiness:
        nextJsonQuery = setRootOfferType(nextJsonQuery)('commercial');
        if (!getTermsValue('office_type')(jsonQuery)?.includes(EOfficeType.Business)) {
          nextJsonQuery = appendTermsValue(nextJsonQuery)('office_type', [EOfficeType.Business]);
        }
        nextJsonQuery = appendTermsValue(nextJsonQuery)('ready_business_types', [
          EJsonQueryReadyBusinessType.RentalBusiness,
        ]);
        break;
      case FOfferType.Building:
        nextJsonQuery = setRootOfferType(nextJsonQuery)('commercial');
        nextJsonQuery = appendTermsValue(nextJsonQuery)('office_type', [EOfficeType.Building]);
        break;
      case FOfferType.DomesticServices:
        nextJsonQuery = setRootOfferType(nextJsonQuery)('commercial');
        nextJsonQuery = appendTermsValue(nextJsonQuery)('office_type', [EOfficeType.DomesticServices]);
        break;
      case FOfferType.CoworkingOffice:
        nextJsonQuery = setRootOfferType(nextJsonQuery)('commercial');
        if (!getTermsValue('office_type')(jsonQuery)?.includes(EOfficeType.Office)) {
          nextJsonQuery = appendTermsValue(nextJsonQuery)('office_type', [EOfficeType.Office]);
        }
        nextJsonQuery = appendTermsValue(nextJsonQuery)('coworking_offer_type', [ECoworkingOfferType.FlexibleOffice]);
        break;
      case FOfferType.CoworkingWorkspace:
        nextJsonQuery = setRootOfferType(nextJsonQuery)('commercial');
        if (!getTermsValue('office_type')(jsonQuery)?.includes(EOfficeType.Office)) {
          nextJsonQuery = appendTermsValue(nextJsonQuery)('office_type', [EOfficeType.Office]);
        }
        nextJsonQuery = appendTermsValue(nextJsonQuery)('coworking_offer_type', [
          ECoworkingOfferType.FixedWorkSpace,
          ECoworkingOfferType.FreeWorkSpace,
        ]);
        break;
      case FOfferType.CommercialLand:
        nextJsonQuery = setRootOfferType(nextJsonQuery)('commercial');
        nextJsonQuery = setTerms('office_type')(nextJsonQuery)(null);
        if (dealType & FDealType.Rent) {
          nextJsonQuery = setTerms('category')(nextJsonQuery)([ECategoryType.CommercialLandRent]);
        } else if (dealType & FDealType.Sale) {
          nextJsonQuery = setTerms('category')(nextJsonQuery)([ECategoryType.CommercialLandSale]);
        }
        break;
    }

    return nextJsonQuery;
  };
}

function setRootOfferType(jsonQuery: TJsonQuery) {
  return (offerType: 'flat' | 'commercial' | 'suburban' | 'newobject'): TJsonQuery => {
    const currentType = jsonQuery._type;

    const nextType: IJsonQueryType = currentType.replace(
      /flat|commercial|suburban|newobject/,
      offerType,
    ) as IJsonQueryType;

    return setRootType(nextType)(jsonQuery);
  };
}
